import appstore from "../../appStore";
import { ApiVehicleOption, DataLoadingStatus, AsyncLoadState } from "./Redux/ConditionEntities";
import { ServiceCheckStatus } from "../Booking/BookingEntities";
import { ParseVehicleConditionHelper } from "./ConditionHelper";
import { ComputeAsyncLoadState } from "./ComputeAsyncLoadStats";
import { ConsiderFareUpdate } from "./FareLoader";
import { Api } from "../../Services/Api";
import { Dispatch } from "../Dispatch";
import { AsyncUpdateOutcome } from "./AsyncUpdateOutcome";
import { Condition } from "../../Services/BookingEntities";
import { vehicleConditionList } from "./PopulateConditions";
import { appInsights } from "../../client";
import { ServiceOffering } from "../../Services/ServiceOfferingsContracts";
import { ServiceOfferingsService } from "../../Services/ServiceOfferings";
import { FeatureFlags } from "../../Config/FeatureFlags";

/**
 * Maps a ServiceOffering from the new GetServiceOfferings API to the internal ApiVehicleOption format.
 * Used when BookingApiV2 feature flag is enabled.
 * Uses the TaxiServiceText from the API response directly as the display name.
 */
function MapServiceOfferingToApiVehicleOption(offering: ServiceOffering): ApiVehicleOption {
    const name = offering.Diagnostics.TaxiServiceText;
    
    // For V2 API: Use VariableCapacity.MaxSeats if provided by API, otherwise use 0
    // Vehicles that need seat selection (Maxi Taxi, Silver Exec XL) always have VariableCapacity
    // Other vehicles (wheelchair, sedan, etc.) get MaxSeat = 0 (display-only, not sent to booking API)
    const maxSeat = offering.VariableCapacity?.MaxSeats ?? 0;
    
    return {
        ApiId: offering.TaxiServiceId,
        ApiVersion: 2,
        Name: name,
        MaxSeat: maxSeat,
        Description: maxSeat > 0 ? `${name} - up to ${maxSeat} passenger${maxSeat !== 1 ? 's' : ''}` : name,
        IsFixedFareAvailable: offering.IsFixedFareAvailable
    };
}

/** Caller signals that the pickup address has changed. Trigger a load of the condition data if necessary. */
export async function ConsiderConditionUpdate(): Promise<AsyncUpdateOutcome>
{
    // input: pickup address + suburb ID
    const appState = appstore.getState();
    const pickup = appState.booking.Locations[0].Address;
    if (!pickup) return AsyncUpdateOutcome.InputsNotReady;

    // prerequisite: location known serviceable
    const cabService = appState.booking.PickupServiceCheck;
    if (cabService.status !== ServiceCheckStatus.KnownGood) return AsyncUpdateOutcome.InputsNotReady;

    // existing loading status
    const suburbIdToCheck = cabService.suburbId!;
    const currentStatus = appState.condition.LoadingStatus;

    // Check if we need to reload: either suburb changed, status is error, OR cityId is missing (e.g., after clearing address)
    const hasCityId = cabService.status === ServiceCheckStatus.KnownGood && cabService.cityId !== undefined;
    const needsReload = !currentStatus.LastInput || 
                        (currentStatus.LastInput !== suburbIdToCheck) || 
                        (currentStatus.Status === DataLoadingStatus.Error) ||
                        (FeatureFlags.BookingApiV2 && !hasCityId);

    if (!needsReload) {
        await ConsiderFareUpdate();
        // this is a duplicate request; not needed
        return AsyncUpdateOutcome.InputsUnchanged;
    }

    // OK!
    return await PerformConditionLoad(suburbIdToCheck);
}

/** Perform the Conditions data load, including setting the state to in progress and then complete / error. */
async function PerformConditionLoad(suburbId: number): Promise<AsyncUpdateOutcome> {

    Dispatch.Condition.ConditionDataLoadStatus({ Status: DataLoadingStatus.InProgress, LastInput: suburbId });

    if (FeatureFlags.BookingApiV2) {
        // NEW: Use GetServiceOfferings endpoint (replaces GetAllConditionsBySuburbId)
        const result = await ServiceOfferingsService.GetServiceOfferings(suburbId);

        // check for input drift (i.e. pickup changed to a new suburb), which would make the result no longer meaningful
        const pickupNow = appstore.getState().booking.PickupServiceCheck;
        if ((pickupNow.status !== ServiceCheckStatus.KnownGood) || (pickupNow.suburbId !== suburbId)) {
            return AsyncUpdateOutcome.InputChangedDuringLoad;
        }

        if (result.isSuccess) {
            // Map new API format to internal format
            const vehicleOptions = result.value.map(MapServiceOfferingToApiVehicleOption);
            const parsedLookupTables = ParseVehicleConditionHelper(vehicleOptions);
            
            Dispatch.Condition.RefreshConditions(parsedLookupTables);
            
            // Store CityId from service offerings for use in GetFareV5
            const cityId = result.value[0]?.CityId;
            if (cityId !== undefined) {
                const currentPickupCheck = appstore.getState().booking.PickupServiceCheck;
                if (currentPickupCheck.status === ServiceCheckStatus.KnownGood) {
                    const pickupPlaceId = appstore.getState().booking.PickupPlaceId;
                    
                    Dispatch.Booking.PickupServiceability({
                        PickupPlaceId: pickupPlaceId,
                        ServiceabilityCheckState: {
                            ...currentPickupCheck,
                            cityId: cityId
                        }
                    });
                }
            }
            
            const newState: AsyncLoadState<number> = {
                Status: DataLoadingStatus.Idle,
                LastInput: suburbId
            };
            Dispatch.Condition.ConditionDataLoadStatus(newState);

            await ConsiderFareUpdate();
            return AsyncUpdateOutcome.Success;
        } else {
            // Handle error from GetServiceOfferings
            const errorMessage = "Failed to load service offerings";
            
            const newState: AsyncLoadState<number> = {
                Status: DataLoadingStatus.Error,
                ErrorMessage: errorMessage,
                LastInput: suburbId
            };
            Dispatch.Condition.ConditionDataLoadStatus(newState);
            appInsights.trackEvent({ name: "Vehicle selection failure" });
            return AsyncUpdateOutcome.LoadFailed;
        }
    } else {
        // OLD: Use existing GetCarsBySuburbId endpoint
        const result = await Api.Booking.GetCarsBySuburbId(suburbId);

        const allConditionList = await Api.Booking.GetAllConditionsBySuburbId(suburbId);
        if (allConditionList.isSuccess) {
            const vehicleOptions = allConditionList.value.map(MakeVehicleOption);
            Dispatch.Condition.LoadAllRawV1Data(vehicleOptions);
        }
        
        // check for input drift (i.e. pickup changed to a new suburb), which would make the result no longer meaningful
        const pickupNow = appstore.getState().booking.PickupServiceCheck;
        if ((pickupNow.status !== ServiceCheckStatus.KnownGood) || (pickupNow.suburbId !== suburbId)) {
            return AsyncUpdateOutcome.InputChangedDuringLoad;
        }

        // push state changes
        const newState = ComputeAsyncLoadState(result, suburbId);
        
        if (result.isSuccess) {
            const vehicleOptions = result.value.map(MakeVehicleOption);
            const parsedLookupTables = ParseVehicleConditionHelper(vehicleOptions);
            Dispatch.Condition.RefreshConditions(parsedLookupTables);
            Dispatch.Condition.ConditionDataLoadStatus(newState);

            // TODO: ideally i would return the Fare's status as well as this one. But currently it would make the return value too awkward.
            await ConsiderFareUpdate();
            return AsyncUpdateOutcome.Success;
        } else {
            Dispatch.Condition.ConditionDataLoadStatus(newState);
            appInsights.trackEvent({ name: "Vehicle selection failure" });
            return AsyncUpdateOutcome.LoadFailed;
        }
    }
}

/**
 * Converts the V1 API format, Condition, into the standard internal format, ApiVehicleOption.
 */
function MakeVehicleOption(condition: Condition): ApiVehicleOption {

    const vehicle: ApiVehicleOption = {
        ApiId: condition.ID,
        ApiVersion: 1,
        Description: condition.Description,
        MaxSeat: condition.MaxSeat,
        Name: condition.Name,
    };

    // 13cabs-specific override of the description for MAXI TAXI. The text coming from the Booking API is a bit confusing.
    const wellKnownVehicle = vehicleConditionList.find(i => i.ApiId === condition.ID);

    if (wellKnownVehicle && wellKnownVehicle.Name === "MAXI TAXI") {
        vehicle.Description = wellKnownVehicle.Description;
    }

    return vehicle;
}
